import React from "react";
import { inject, observer } from "mobx-react";

import {
  Table,
  TableHead,
  TableCell,
  TableRow,
  TableBody,
  Grid,
  Paper,
  Typography,
  withStyles,
} from "@material-ui/core";
import { IManagementStore } from "Stores/Management/types";
import ResultsAreEmpty from "@src/Components/Ui/ResultsAreEmpty";
import { Values } from "@src/Tools/Values";
import { STTableHeadTypography } from "@src/Components/Ui/STTableHeadTypography";
import EntityDelete from "@src/Components/Common/EntityDelete/EntityDelete";
import { IValuesStore } from "@src/Stores/Values/types";
import AddAuthApiToken from "./AddAuthApiToken/AddAuthApiToken";
import { IAuthStore } from "@src/Stores/Auth/types";
import { STTableCellTypography } from "@src/Components/Ui/STTableCellTypography";
import AuthTokensHelpModal from "./AuthTokensHelpModal/AuthTokensHelpModal";
import { STGrid } from "./styles";
import { computed } from "mobx";
import { formatDistance } from "date-fns";
import { IUserApiTokenInfo } from "@src/Interfaces/Entity/UserApiToken";
import ContentCopyRoundedIcon from "@material-ui/icons/FileCopy";
import RoundButton from "@src/Shared/Components/RoundButton/RoundButton";
import { Toast } from "@src/Tools/Toast";

interface IProps {
  management?: IManagementStore;
  values?: IValuesStore;
  auth?: IAuthStore;
  classes?: any;
}

const styles = {
  input: {
    color: "black !important",
    cursor: "pointer !important",
    fontSize: "0.9rem",
  },
};

@inject("auth", "management", "values")
@observer
class AuthApiTokensContent extends React.Component<IProps> {
  @computed get userApiTokens() {
    const currentLocation = this.props.auth.user.location;
      return this.props.management.userApiTokens;
  }

  getTokenLastUsedString(token: IUserApiTokenInfo) {
    if (!token.lastUsed) {
      return "-";
      }

    let formatted = formatDistance(new Date(token.lastUsed), new Date(), {
    addSuffix: true,
    });

    return formatted.charAt(0).toUpperCase() + formatted.slice(1);
  }

  getTokenCreatedOnFormattedString(token: IUserApiTokenInfo) {
    if (!token.createdOn) {
      return "-";
    }
    const options = {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
    };

     const dateTime = new Date(token.createdOn);
     return new Intl.DateTimeFormat('en-GB', options).format(dateTime).replace(/\//g, '-');;
  }

  getExpirationString(token: IUserApiTokenInfo) {
    if (!token.expiresAt) {
      return "-";
    }

    let formatted = formatDistance(new Date(token.expiresAt), new Date(), {
    addSuffix: true,
    });

    return formatted.charAt(0).toUpperCase() + formatted.slice(1);
  }

  handleRemove = async (id: string) => {
    await this.props.management.removeUserApiToken(id);
    };

  handleTokenCopy = async (token: IUserApiTokenInfo) => {
    const toast = new Toast();
    if (token.isReceived) {
        toast.showWarning(Values.getLocalizationValue("tokenAlreadyCopiedOnceMessage"));
        return;
    }
    try {
        const res = await this.props.management.getDescryptedUserApiToken(token.id);
        const textArea = document.createElement('textarea');
        textArea.value = res.token;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);        
        toast.showSuccess(Values.getLocalizationValue("tokenCopiedToClipboardMessage"));
      } catch (error) {
        toast.showError(Values.getLocalizationValue("tokenCopyingErrorMessage"));
      }
  };

  render() {
    return (
      <Grid container direction="column">
        <Grid container wrap="nowrap" justify="flex-end" item>
          <Grid item xs={6}>
            <STGrid container justify="flex-start">
              <AuthTokensHelpModal />
            </STGrid>
          </Grid>
          <Grid item xs={6}>
            <Grid container justify="flex-end">
              <AddAuthApiToken />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs>
          {this.userApiTokens.length > 0 ? (
            <Paper elevation={2}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell align="left">
                      <STTableHeadTypography>{Values.getLocalizationValue("title")}</STTableHeadTypography>
                    </TableCell>
                     <TableCell align="left">
                      <STTableHeadTypography>{Values.getLocalizationValue("tokenCreated")}</STTableHeadTypography>
                    </TableCell>
                    <TableCell align="left">
                      <STTableHeadTypography>{Values.getLocalizationValue("tokenExpires")}</STTableHeadTypography>
                    </TableCell>
                    <TableCell align="left">
                      <STTableHeadTypography>{Values.getLocalizationValue("tokenLastUsed")}</STTableHeadTypography>
                    </TableCell>
                    <TableCell align="right" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {this.userApiTokens.map((token: IUserApiTokenInfo) => {
                    return (
                      <TableRow key={token.id}>
                        <TableCell component="th" scope="row" size="small" align="left">
                          <Typography>
                            <STTableCellTypography>{token.name}</STTableCellTypography>
                          </Typography>
                            </TableCell>
                         <TableCell component="th" scope="row" size="small" align="center">
                          <Typography>
                            <STTableCellTypography>
                              {this.getTokenCreatedOnFormattedString(token)}
                            </STTableCellTypography>
                          </Typography>
                        </TableCell>
                        <TableCell component="th" scope="row" size="small" align="center">
                          <Typography>
                            <STTableCellTypography>
                              {this.getExpirationString(token)}
                            </STTableCellTypography>
                          </Typography>
                        </TableCell>
                        <TableCell component="th" scope="row" size="small" align="center">
                          <Typography>
                            <STTableCellTypography>
                              {this.getTokenLastUsedString(token)}
                            </STTableCellTypography>
                          </Typography>
                        </TableCell>
                        <TableCell align="right" size="small">
                            <Grid container wrap="nowrap" justify="flex-end">
                            <RoundButton
                                icon={<ContentCopyRoundedIcon />}
                                tooltipTitle={Values.getLocalizationValue("copy")}
                                color={"gray"}
                                onClick={() =>this.handleTokenCopy(token)}
                            />
                              <EntityDelete onSubmit={() => this.handleRemove(token.id)} />
                            </Grid>
                        </TableCell>                        
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Paper>
          ) : (
            <ResultsAreEmpty />
          )}
        </Grid>
      </Grid>
    );
  }
}

export default withStyles(styles)(AuthApiTokensContent);
